import { graphql } from 'react-relay';
import { JiraIssueFieldOptionAri } from '@atlassian/ari/jira/issue-field-option';
import type { cascadingSelectIssueTransitionIssueAdjustmentsQuery as cascadingSelectIssueTransitionIssueAdjustmentsQueryType } from '@atlassian/jira-relay/src/__generated__/cascadingSelectIssueTransitionIssueAdjustmentsQuery.graphql';
import { FieldValueLookupError } from '../../../common/errors';
import type { CascadingSelectPublicSetValue } from '../../../common/fields/cascading-select';
import type { PublicToInternalPropertyTransformerContext } from '../../../common/types';
import { fetchFieldOptions } from '../../../common/utils/fetch-field-options';
import { getResourceIdfromAri } from '../../../common/utils/get-resource-id-from-ari';

type CascadingSelectPublicGetValue = {
	parent: { id: string; value: string };
	child: { id: string; value: string } | null;
} | null;

type CascadingSelectInternalValue = {
	parentOptionValue: {
		id: string;
		value: string;
	};
	childOptionValue: {
		id: string;
		value: string;
	} | null;
} | null;

// eslint-disable-next-line jira/relay/must-use-inline
export const cascadingSelectIssueTransitionIssueAdjustmentsQuery = graphql`
	query cascadingSelectIssueTransitionIssueAdjustmentsQuery(
		$ari: ID!
		$filterById: JiraFieldOptionIdsFilterInput
		$filterByIdChild: JiraFieldOptionIdsFilterInput
	) {
		node(id: $ari) {
			... on JiraCascadingSelectField {
				# eslint-disable-next-line relay/unused-fields
				parentOptions(filterById: $filterById) @optIn(to: "JiraCascadingParentOptions") {
					edges {
						node {
							id
							value
							childOptions(filterById: $filterByIdChild) {
								edges {
									node {
										id
										value
									}
								}
							}
						}
					}
				}
			}
		}
	}
`;

export const transformSetValueCascadingSelect = async (
	inputValue: CascadingSelectPublicSetValue,
	_lookupValues: undefined,
	{
		tenantContext,
		viewContext,
		environment,
		fieldContext,
	}: PublicToInternalPropertyTransformerContext,
): Promise<CascadingSelectInternalValue> => {
	if (inputValue === null) {
		return null;
	}
	let childAri = null;

	const parentAri = JiraIssueFieldOptionAri.create({
		siteId: tenantContext.cloudId,
		activationId: tenantContext.activationId,
		fieldId: fieldContext.fieldId,
		optionId: inputValue.parentId,
	}).toString();

	if (inputValue?.childId) {
		childAri = JiraIssueFieldOptionAri.create({
			siteId: tenantContext.cloudId,
			activationId: tenantContext.activationId,
			fieldId: fieldContext.fieldId,
			optionId: inputValue.childId,
		}).toString();
	}

	const response: NonNullable<
		NonNullable<
			cascadingSelectIssueTransitionIssueAdjustmentsQueryType['response']['node']
		>['parentOptions']
	> = await fetchFieldOptions<cascadingSelectIssueTransitionIssueAdjustmentsQueryType>({
		tenantContext,
		viewContext,
		environment,
		fieldContext,
		optionARIs: [parentAri],
		nodePropertyName: 'parentOptions',
		queryName: 'FETCH_CASCADING_SELECT',
		query: cascadingSelectIssueTransitionIssueAdjustmentsQuery,
		extraQueryVariables: {
			filterByIdChild: {
				optionIds: childAri ? [childAri] : [],
				operation: 'ALLOW',
			},
		},
	});

	const parentOption = response?.edges?.[0]?.node;
	const childOption = response?.edges?.[0]?.node?.childOptions?.edges?.[0]?.node;

	if (parentOption?.id !== parentAri || (inputValue?.childId && childOption?.id !== childAri)) {
		throw new FieldValueLookupError(JSON.stringify(inputValue));
	}

	return {
		parentOptionValue: {
			id: parentOption.id,
			value: parentOption?.value ?? '',
		},
		childOptionValue:
			inputValue?.childId && childOption
				? {
						id: childOption.id,
						value: childOption?.value ?? '',
					}
				: null,
	};
};

export const transformGetValueCascadingSelect = (
	inputValue: CascadingSelectInternalValue,
): CascadingSelectPublicGetValue => {
	if (!inputValue) {
		return null;
	}

	const parent = {
		id: getResourceIdfromAri(inputValue.parentOptionValue.id),
		value: inputValue.parentOptionValue.value,
	};
	const child = inputValue.childOptionValue
		? {
				id: getResourceIdfromAri(inputValue.childOptionValue.id),
				value: inputValue.childOptionValue.value,
			}
		: null;

	return { parent, child };
};
